/**
 * ============================================================================
 * DPoP (Demonstrating Proof-of-Possession) Setup - Frontend Implementation
 * ============================================================================
 * 
 * Target Spec: RFC 9449 (OAuth 2.0 Demonstrating Proof-of-Possession)
 * 
 * Features & Fixes Included:
 * 1. Safe Web Crypto API detection with graceful fallback for HTTP LAN IP testing.
 * 2. Non-extractable ECDSA (P-256) keypair generated & stored in IndexedDB on HTTPS / localhost.
 * 3. Single-flight Promise lock to eliminate key generation race conditions on app load.
 * 4. Exported `getDevicePublicJwk()` for attaching public JWK during Login/Signin calls.
 * 5. RFC 9449 compliant `htu` (HTTP URI without query string or hash).
 * 6. Robust Token Reader (`getStoredAccessToken`) with sessionStorage/localStorage fallbacks & crypto decryption.
 * 7. Bulletproof origin detection with quote stripping & 3rd-party domain exclusion.
 * 8. Safe `window.fetch` override preserving existing Request object headers.
 * 9. Automatic `axios` request interceptor for default & custom instances (`axios.create`).
 */

import axios from "axios";

// Database & Store constants for IndexedDB
const DB_NAME = "DPoP_KeyStoreDB";
const DB_VERSION = 1;
const STORE_NAME = "dpop_keys";
const KEY_ALIAS = "dpop_keypair";

// In-memory cache for loaded keypair and public JWK
let cachedKeyPair = null;
let cachedPublicJwk = null;

// Single-flight Promise Lock to prevent parallel key-generation race conditions on app load
let keyPairPromise = null;

/**
 * Checks if Web Crypto API (crypto.subtle) is available in current browser context.
 * Browsers enable window.crypto.subtle on localhost or HTTPS contexts.
 */
function isSubtleCryptoSupported() {
  return typeof window !== "undefined" && window.crypto && typeof window.crypto.subtle !== "undefined";
}

/**
 * ----------------------------------------------------------------------------
 * 1. IndexedDB Helper & Key Generation with Race-Condition Lock
 * ----------------------------------------------------------------------------
 */
function openDPoPDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      return reject(new Error("IndexedDB is not supported in this browser environment."));
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = (event) => reject(event.target.error);
  });
}

/**
 * Retrieves existing keypair from IndexedDB or generates a new non-extractable keypair.
 * Uses a single-flight promise lock (`keyPairPromise`) to ensure concurrent calls await the same generation task.
 */
async function getOrGenerateKeyPair() {
  if (!isSubtleCryptoSupported()) {
    console.warn("DPoP Setup: Web Crypto API (window.crypto.subtle) is disabled in this unsecure context (HTTP LAN IP). Skipping DPoP key generation.");
    return null;
  }

  if (cachedKeyPair) {
    return cachedKeyPair;
  }

  if (keyPairPromise) {
    return keyPairPromise;
  }

  keyPairPromise = (async () => {
    try {
      const db = await openDPoPDB();

      // Check if keypair already exists in IndexedDB
      const txRead = db.transaction(STORE_NAME, "readonly");
      const storeRead = txRead.objectStore(STORE_NAME);
      
      const existingKeyPair = await new Promise((resolve, reject) => {
        const req = storeRead.get(KEY_ALIAS);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });

      if (existingKeyPair && existingKeyPair.privateKey && existingKeyPair.publicKey) {
        cachedKeyPair = existingKeyPair;
        return cachedKeyPair;
      }

      // Generate a new ECDSA P-256 keypair
      // extractable: false guarantees the private key CANNOT be exported out of browser!
      const newKeyPair = await window.crypto.subtle.generateKey(
        {
          name: "ECDSA",
          namedCurve: "P-256",
        },
        false, // extractable = false (SECURITY MANDATE)
        ["sign", "verify"]
      );

      // Save keypair directly to IndexedDB
      const txWrite = db.transaction(STORE_NAME, "readwrite");
      const storeWrite = txWrite.objectStore(STORE_NAME);
      await new Promise((resolve, reject) => {
        const req = storeWrite.put(newKeyPair, KEY_ALIAS);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });

      cachedKeyPair = newKeyPair;
      return cachedKeyPair;
    } catch (error) {
      console.error("DPoP Setup: Failed to get/generate ECDSA keypair from IndexedDB:", error);
      return null;
    }
  })();

  try {
    return await keyPairPromise;
  } finally {
    keyPairPromise = null;
  }
}

/**
 * Exports public key to JSON Web Key (JWK) format.
 */
async function getPublicJwk(publicKey) {
  if (!publicKey || !isSubtleCryptoSupported()) {
    return null;
  }
  if (cachedPublicJwk) {
    return cachedPublicJwk;
  }
  try {
    const jwk = await window.crypto.subtle.exportKey("jwk", publicKey);
    cachedPublicJwk = {
      kty: jwk.kty,
      crv: jwk.crv,
      x: jwk.x,
      y: jwk.y,
    };
    return cachedPublicJwk;
  } catch (e) {
    return null;
  }
}

/**
 * Exported function to get Device Public JWK for Login / Registration request payload
 */
export async function getDevicePublicJwk() {
  const keyPair = await getOrGenerateKeyPair();
  if (!keyPair || !keyPair.publicKey) {
    return null;
  }
  return getPublicJwk(keyPair.publicKey);
}

/**
 * ----------------------------------------------------------------------------
 * 2. Helper Utilities (Base64URL, SHA-256 Digest, ATH Computation)
 * ----------------------------------------------------------------------------
 */
function base64UrlEncode(input) {
  let base64 = "";
  if (typeof input === "string") {
    base64 = btoa(unescape(encodeURIComponent(input)));
  } else {
    const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    base64 = btoa(binary);
  }
  return base64
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Computes `ath` (Access Token Hash) = base64url(SHA-256(ASCII(access_token))) as per RFC 9449 Section 4.2.
 */
async function computeAth(accessToken) {
  if (!accessToken || typeof accessToken !== "string" || !isSubtleCryptoSupported()) return undefined;
  
  const rawToken = accessToken.replace(/^Bearer\s+/i, "").trim();
  if (!rawToken) return undefined;

  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(rawToken);
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
    return base64UrlEncode(hashBuffer);
  } catch (e) {
    return undefined;
  }
}

/**
 * Standardized HTU (HTTP URI without query string or fragment) as required by RFC 9449 Section 4.2.
 */
function getHtu(fullUrl) {
  try {
    const urlObj = new URL(fullUrl, window.location.href);
    return `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`;
  } catch (e) {
    return fullUrl.split("?")[0].split("#")[0];
  }
}

/**
 * ----------------------------------------------------------------------------
 * 3. Origin Filtering (Supports CRA & Vite Environment Variables & Excludes 3rd Party)
 * ----------------------------------------------------------------------------
 */
function isBackendOrigin(url) {
  if (!url) return false;
  try {
    const targetUrlObj = new URL(url, window.location.href);
    const targetOrigin = targetUrlObj.origin.toLowerCase();
    const pathname = targetUrlObj.pathname.toLowerCase();

    // Skip static image files and image proxy CDNs from DPoP header attachment
    if (/\.(png|jpe?g|gif|svg|webp|ico|bmp|tiff)(\?.*)?$/i.test(pathname)) {
      return false;
    }
    if (/weserv\.nl|corsproxy\.io|cloudfront\.net/i.test(targetUrlObj.hostname)) {
      return false;
    }

    // Read Environment API URL
    let envApiUrl = null;
    if (typeof process !== "undefined" && process.env) {
      envApiUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL;
    }
    if (!envApiUrl && typeof import.meta !== "undefined" && import.meta.env) {
      envApiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL;
    }

    if (envApiUrl && typeof envApiUrl === "string") {
      // Clean quotes & whitespace from .env strings
      envApiUrl = envApiUrl.trim().replace(/^['"]|['"]$/g, "");
      try {
        const envOrigin = new URL(envApiUrl, window.location.href).origin.toLowerCase();
        if (targetOrigin === envOrigin) {
          return true;
        }
      } catch (e) {}
    }

    // Match frontend origin for relative URLs or local proxy requests
    if (targetOrigin === window.location.origin.toLowerCase()) {
      return true;
    }

    // Check if target URL is a known external 3rd-party service to skip
    const isThirdParty = /razorpay\.com|amazonaws\.com|google\.com|firebaseio\.com/i.test(targetUrlObj.hostname);
    if (!isThirdParty) {
      return true;
    }

    return false;
  } catch (e) {
    return false;
  }
}

/**
 * ----------------------------------------------------------------------------
 * 4. Token Storage Reader (sessionStorage & localStorage Fallbacks with Decryption)
 * ----------------------------------------------------------------------------
 */
function getStoredAccessToken() {
  if (typeof window === "undefined") return null;

  const keysToTry = ["token", "accessToken", "access_token", "authToken"];
  const storages = [window.sessionStorage, window.localStorage];

  let decryptDataFn = null;
  try {
    const cryptoModule = require("./crypto");
    decryptDataFn = cryptoModule.decryptData || cryptoModule.default;
  } catch (e) {
    // Crypto module optional / missing fallback
  }

  for (const storage of storages) {
    if (!storage) continue;
    for (const key of keysToTry) {
      const rawValue = storage.getItem(key);
      if (rawValue) {
        if (decryptDataFn) {
          try {
            const decrypted = decryptDataFn(rawValue);
            if (decrypted) return decrypted;
          } catch (e) {}
        }
        return rawValue;
      }
    }
  }

  return null;
}

/**
 * ----------------------------------------------------------------------------
 * 5. DPoP Proof JWT Generator
 * ----------------------------------------------------------------------------
 */
export async function generateDPoPProof(method, fullUrl, accessToken = null) {
  if (!isSubtleCryptoSupported()) {
    return null;
  }

  const keyPair = await getOrGenerateKeyPair();
  if (!keyPair || !keyPair.publicKey || !keyPair.privateKey) {
    return null;
  }

  const publicJwk = await getPublicJwk(keyPair.publicKey);
  if (!publicJwk) {
    return null;
  }

  const htm = (method || "GET").toUpperCase();
  const htu = getHtu(fullUrl);

  const header = {
    typ: "dpop+jwt",
    alg: "ES256",
    jwk: publicJwk,
  };

  const payload = {
    htm: htm,
    htu: htu,
    iat: Math.floor(Date.now() / 1000),
    jti: window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : Math.random().toString(36).substring(2) + Date.now().toString(36),
  };

  const ath = await computeAth(accessToken);
  if (ath) {
    payload.ath = ath;
  }

  const encodedHeader = base64UrlEncode(JSON.stringify(header));
  const encodedPayload = base64UrlEncode(JSON.stringify(payload));
  const signingInput = `${encodedHeader}.${encodedPayload}`;

  const signatureBuffer = await window.crypto.subtle.sign(
    {
      name: "ECDSA",
      hash: { name: "SHA-256" },
    },
    keyPair.privateKey,
    new TextEncoder().encode(signingInput)
  );

  const encodedSignature = base64UrlEncode(signatureBuffer);
  return `${signingInput}.${encodedSignature}`;
}

/**
 * ----------------------------------------------------------------------------
 * 6. Axios Interceptor Patching & axios.create Wrapping
 * ----------------------------------------------------------------------------
 */
export function attachDPoPToAxiosInstance(instance) {
  if (!instance || instance.__dpopAttached) return;
  instance.__dpopAttached = true;

  instance.interceptors.request.use(
    async (config) => {
      try {
        let requestUrl = config.url || "";
        if (!/^https?:\/\//i.test(requestUrl)) {
          let baseURL = config.baseURL;
          if (!baseURL) {
            if (typeof process !== "undefined" && process.env) {
              baseURL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL;
            }
            if (!baseURL && typeof import.meta !== "undefined" && import.meta.env) {
              baseURL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL;
            }
          }
          if (baseURL && typeof baseURL === "string") {
            baseURL = baseURL.trim().replace(/^['"]|['"]$/g, "");
          }
          baseURL = baseURL || window.location.origin;
          requestUrl = new URL(requestUrl, baseURL).href;
        }

        if (isBackendOrigin(requestUrl)) {
          let authHeader = config.headers?.Authorization || config.headers?.authorization;
          let token = authHeader || getStoredAccessToken();

          const method = config.method || "GET";
          const dpopProof = await generateDPoPProof(method, requestUrl, token);

          if (dpopProof) {
            if (!config.headers) {
              config.headers = {};
            }

            if (typeof config.headers.set === "function") {
              if (typeof config.headers.delete === "function") {
                config.headers.delete("dpop");
              }
              config.headers.set("DPoP", dpopProof);
            } else {
              delete config.headers["dpop"];
              config.headers["DPoP"] = dpopProof;
            }
          }
        }
      } catch (err) {
        console.error("DPoP Setup: Error attaching DPoP header to Axios request:", err);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
}

// Attach interceptor to default global axios instance
attachDPoPToAxiosInstance(axios);

// Wrap axios.create so any custom instance created across files gets interceptor automatically
const originalAxiosCreate = axios.create;
if (originalAxiosCreate) {
  axios.create = function (...args) {
    const instance = originalAxiosCreate.apply(this, args);
    attachDPoPToAxiosInstance(instance);
    return instance;
  };
}

/**
 * ----------------------------------------------------------------------------
 * 7. window.fetch Interceptor Override with Safe Request Object Header Merging
 * ----------------------------------------------------------------------------
 */
if (typeof window !== "undefined" && window.fetch) {
  const originalFetch = window.fetch;
  window.fetch = async function (input, init = {}) {
    try {
      let requestUrl = typeof input === "string" ? input : (input instanceof Request ? input.url : String(input));
      let fullUrl = new URL(requestUrl, window.location.href).href;

      if (isBackendOrigin(fullUrl)) {
        let method = (init && init.method) || (input instanceof Request ? input.method : "GET");

        let headersObj = {};
        if (input instanceof Request && input.headers) {
          input.headers.forEach((val, key) => {
            headersObj[key] = val;
          });
        }

        if (init && init.headers) {
          if (init.headers instanceof Headers) {
            init.headers.forEach((val, key) => {
              headersObj[key] = val;
            });
          } else if (Array.isArray(init.headers)) {
            init.headers.forEach(([k, v]) => {
              headersObj[k] = v;
            });
          } else if (typeof init.headers === "object") {
            Object.assign(headersObj, init.headers);
          }
        }

        let authHeader = headersObj["Authorization"] || headersObj["authorization"];
        let token = authHeader || getStoredAccessToken();
        const dpopProof = await generateDPoPProof(method, fullUrl, token);

        if (dpopProof) {
          delete headersObj["dpop"];
          delete headersObj["DPoP"];
          headersObj["DPoP"] = dpopProof;
          init = { ...init, headers: headersObj };
        }
      }
    } catch (err) {
      console.error("DPoP Setup: Error attaching DPoP header to fetch request:", err);
    }

    return originalFetch.call(this, input, init);
  };
}
